import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { computed, effect, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { pipe } from 'rxjs';
import { switchMap, tap, map } from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ModalService } from '../../components/ui/modalService/ModalService';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../../environments/environment';
import { ChangelogEntry, parseChangelog } from '../../functions/parse-changelog';

const SEEN_VERSION_KEY = 'changelog-seen-version';

type ChangelogState = {
  entries: ChangelogEntry[];
  isLoading: boolean;
  loaded: boolean;
};

const initialState: ChangelogState = {
  entries: [],
  isLoading: false,
  loaded: false,
};

export const changelogStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ entries }) => ({
    latestEntry: computed(() => entries()[0] ?? null),
  })),
  withMethods((store, http = inject(HttpClient), modalService = inject(ModalService)) => ({
    fetchChangelog: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() =>
          http.get('/assets/CHANGELOG.md', { responseType: 'text' }).pipe(
            map(parseChangelog),
            tapResponse({
              next: (entries) => patchState(store, { entries, isLoading: false, loaded: true }),
              error: (err) => {
                console.error('Fehler beim Laden des Changelogs', err);
                patchState(store, { isLoading: false, loaded: true });
              },
            }),
          ),
        ),
      ),
    ),
    loadChangelog(): void {
      if (store.loaded() || store.isLoading()) {
        return;
      }
      this.fetchChangelog();
    },
    async checkAndShowWhatsNew(): Promise<void> {
      const seenVersion = localStorage.getItem(SEEN_VERSION_KEY);

      if (seenVersion === null) {
        localStorage.setItem(SEEN_VERSION_KEY, environment.version);
        return;
      }

      if (seenVersion === environment.version) {
        return;
      }

      this.loadChangelog();
      const { WhatsNewModal } = await import('../../components/ui/whatsNewModal/whatsNewModal');
      modalService.open({ title: 'Was ist neu?', message: '', variante: 'editor', component: WhatsNewModal });
      localStorage.setItem(SEEN_VERSION_KEY, environment.version);
    },
  })),
  withHooks({
    onInit(store) {
      const auth = inject(AuthService);

      let sessionInitialized = false;

      effect(() => {
        const loading = auth.loading();
        const loggedIn = auth.loggedIn();

        if (loading) {
          return;
        }

        if (!loggedIn) {
          sessionInitialized = false;
          return;
        }

        if (sessionInitialized) {
          return;
        }

        sessionInitialized = true;
        store.checkAndShowWhatsNew();
      });
    },
  }),
);
